import React from 'react';
import { Popover, Checkbox, Button, Space, Typography } from 'antd';
import { SettingOutlined, UndoOutlined } from '@ant-design/icons';

const { Text } = Typography;

/**
 * ColumnSelector Component
 * Allows users to check/uncheck table columns via a Popover menu.
 *
 * @param {Array} allColumns - Full list of column objects (with title, dataIndex/key)
 * @param {Array} visibleKeys - List of currently checked column keys
 * @param {Function} setVisibleKeys - Setter function for checked column keys
 * @param {Function} onReset - Function to reset all columns back to visible
 */
const ColumnSelector = ({ allColumns = [], visibleKeys = [], setVisibleKeys, onReset }) => {
  const options = allColumns.map((col) => ({
    label: col.title || col.header || String(col.dataIndex || col.key),
    value: String(col.dataIndex || col.key),
  }));

  const handleSelectAllChange = (e) => {
    if (e.target.checked) {
      setVisibleKeys(allColumns.map((col) => String(col.dataIndex || col.key)));
    } else {
      // Keep at least the first column checked to prevent empty tables
      const firstKey = String(allColumns[0]?.dataIndex || allColumns[0]?.key || '');
      setVisibleKeys(firstKey ? [firstKey] : []);
    }
  };

  const isAllChecked = options.length > 0 && visibleKeys.length === options.length;
  const isIndeterminate = visibleKeys.length > 0 && visibleKeys.length < options.length;

  const content = (
    <div style={{ minWidth: 260, maxWidth: 320, padding: '4px 0' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #f0f0f0',
          paddingBottom: 8,
          marginBottom: 8,
        }}
      >
        <Checkbox
          indeterminate={isIndeterminate}
          checked={isAllChecked}
          onChange={handleSelectAllChange}
        >
          <Text strong>Select All Columns</Text>
        </Checkbox>
        {onReset && (
          <Button
            type="link"
            size="small"
            icon={<UndoOutlined />}
            onClick={onReset}
            style={{ padding: 0 }}
          >
            Reset
          </Button>
        )}
      </div>

      <div style={{ maxHeight: 320, overflowY: 'auto', paddingRight: 4 }}>
        <Checkbox.Group
          options={options}
          value={visibleKeys}
          onChange={(checkedValues) => setVisibleKeys(checkedValues)}
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        />
      </div>
    </div>
  );

  return (
    <Popover content={content} trigger="click" placement="bottomRight">
      <Button className="cip-btn-submit" icon={<SettingOutlined />}>
        Columns ({visibleKeys.length}/{allColumns.length})
      </Button>
    </Popover>
  );
};

export default ColumnSelector;
